import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Book, Section, Content, ContentType, SectionType, SectionTheme } from '../types';
import { adminApi } from '../services/api';

interface AppContextType {
  // State
  books: Book[];
  contentTypes: ContentType[];
  sectionTypes: SectionType[];
  sectionThemes: SectionTheme[];
  currentSection: Section | null;
  currentContent: Content[];
  loading: boolean;
  error: string | null;
  
  // Actions
  loadBooks: () => Promise<void>;
  loadContentTypes: () => Promise<void>;
  loadSectionTypes: () => Promise<void>;
  loadSectionThemes: () => Promise<void>;
  selectSection: (section: Section, preserveScroll?: boolean) => Promise<void>;
  createBook: (book: any) => Promise<void>;
  createSection: (bookId: string, section: any) => Promise<void>;
  updateContent: (id: string, updates: any) => Promise<void>;
  deleteContent: (id: string) => Promise<void>;
  addContent: (content: any) => Promise<void>;
  addContentAfter: (afterId: string, content: any) => Promise<void>;
  reorderContent: (id: string, direction: 'up' | 'down') => Promise<void>;
  showMessage: (message: string, type: 'success' | 'error') => void;
  clearMessage: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [books, setBooks] = useState<Book[]>([]);
  const [contentTypes, setContentTypes] = useState<ContentType[]>([]);
  const [sectionTypes, setSectionTypes] = useState<SectionType[]>([]);
  const [sectionThemes, setSectionThemes] = useState<SectionTheme[]>([]);
  const [currentSection, setCurrentSection] = useState<Section | null>(null);
  const [currentContent, setCurrentContent] = useState<Content[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadBooks(), loadContentTypes(), loadSectionTypes(), loadSectionThemes()]);
    } catch (err) {
      console.error('Failed to load initial data:', err);
      setError('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  const loadBooks = async () => {
    try {
      const data = await adminApi.getBooks();
      setBooks(data);
    } catch (err) {
      console.error('Failed to load books:', err);
      throw err;
    }
  };

  const loadContentTypes = async () => {
    try {
      const data = await adminApi.getContentTypes();
      setContentTypes(data);
    } catch (err) {
      console.error('Failed to load content types:', err);
      throw err;
    }
  };

  const loadSectionTypes = async () => {
    try {
      const data = await adminApi.getSectionTypes();
      setSectionTypes(data);
    } catch (err) {
      console.error('Failed to load section types:', err);
      throw err;
    }
  };

  const loadSectionThemes = async () => {
    try {
      const data = await adminApi.getSectionThemes();
      setSectionThemes(data);
    } catch (err) {
      console.error('Failed to load section themes:', err);
      throw err;
    }
  };

  const selectSection = async (section: Section, preserveScroll: boolean = false) => {
    const scrollPosition = preserveScroll ? window.scrollY : 0;
    setCurrentSection(section);
    setLoading(true);
    try {
      const content = await adminApi.getSectionContent(section.id);
      setCurrentContent(content);
      if (preserveScroll) {
        // Restore scroll position after React re-renders
        setTimeout(() => {
          window.scrollTo(0, scrollPosition);
        }, 0);
      }
    } catch (err) {
      console.error('Failed to load section content:', err);
      showMessage('Failed to load section content', 'error');
    } finally {
      setLoading(false);
    }
  };

  const createBook = async (bookData: any) => {
    try {
      await adminApi.createBook(bookData);
      await loadBooks();
      showMessage('Book created successfully', 'success');
    } catch (err) {
      console.error('Failed to create book:', err);
      showMessage('Failed to create book', 'error');
      throw err;
    }
  };

  const createSection = async (bookId: string, sectionData: any) => {
    try {
      const section = await adminApi.createSection({ ...sectionData, book_id: bookId });
      await loadBooks();
      showMessage('Section created successfully', 'success');
      // Auto-select the new section
      await selectSection(section);
    } catch (err) {
      console.error('Failed to create section:', err);
      showMessage('Failed to create section', 'error');
      throw err;
    }
  };

  const updateContent = async (id: string, updates: any) => {
    try {
      await adminApi.updateContent(id, updates);
      if (currentSection) {
        await selectSection(currentSection, true);
      }
      showMessage('Content updated successfully', 'success');
    } catch (err) {
      console.error('Failed to update content:', err);
      showMessage('Failed to update content', 'error');
      throw err;
    }
  };

  const deleteContent = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this content?')) {
      return;
    }
    
    try {
      await adminApi.deleteContent(id);
      if (currentSection) {
        await selectSection(currentSection, true);
      }
      showMessage('Content deleted successfully', 'success');
    } catch (err) {
      console.error('Failed to delete content:', err);
      showMessage('Failed to delete content', 'error');
      throw err;
    }
  };

  const addContent = async (contentData: any) => {
    if (!currentSection) return;
    
    try {
      await adminApi.createContent({
        section_id: currentSection.id,
        title: contentData.title || 'New Content',
        content_text: contentData.content_text || '<p>Enter your content here...</p>',
        content_type_id: contentData.content_type_id,
      });
      await selectSection(currentSection, true);
      showMessage('Content added successfully', 'success');
    } catch (err) {
      console.error('Failed to add content:', err);
      showMessage('Failed to add content', 'error');
      throw err;
    }
  };

  const addContentAfter = async (afterId: string, contentData: any) => {
    if (!currentSection) return;
    
    try {
      await adminApi.insertContent(currentSection.id, afterId, {
        title: contentData.title || 'New Content',
        content_text: contentData.content_text || '<p>Enter your content here...</p>',
      });
      await selectSection(currentSection, true);
      showMessage('Content inserted successfully', 'success');
    } catch (err) {
      console.error('Failed to insert content:', err);
      showMessage('Failed to insert content', 'error');
      throw err;
    }
  };

  const reorderContent = async (id: string, direction: 'up' | 'down') => {
    try {
      await adminApi.reorderContent(id, direction);
      if (currentSection) {
        await selectSection(currentSection, true);
      }
    } catch (err) {
      console.error('Failed to reorder content:', err);
      showMessage('Failed to reorder content', 'error');
      throw err;
    }
  };

  const showMessage = (text: string, type: 'success' | 'error') => {
    setMessage({ text, type });
  };

  const clearMessage = () => {
    setMessage(null);
  };

  const value: AppContextType = {
    books,
    contentTypes,
    sectionTypes,
    sectionThemes,
    currentSection,
    currentContent,
    loading,
    error,
    loadBooks,
    loadContentTypes,
    loadSectionTypes,
    loadSectionThemes,
    selectSection,
    createBook,
    createSection,
    updateContent,
    deleteContent,
    addContent,
    addContentAfter,
    reorderContent,
    showMessage,
    clearMessage,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}
    </AppContext.Provider>
  );
};